import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Toast, Popconfirm, Tooltip } from '@douyinfe/semi-ui';
import { IconEdit, IconKey, IconDelete, IconStop, IconPlayCircle, IconPlus, IconUserGroup, IconEyeOpened, IconEyeClosed, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';

const SubAccounts = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const statusMap = {
        1: { text: '启用', type: 'success' },
        2: { text: '禁用', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/subsp/list_sub_sp/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []); // 移除 searchKey 和 searchStatus 依赖

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/subsp/add_sub_sp/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/subsp/edit_sub_sp/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/subsp/delete_sub_sp/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handlePasswordChange = async (values) => {
        try {
            const response = await request.post('/go/sp/subsp/edit_sub_sp_passwd/', {
                uid: currentRecord.uid,
                passwd: values.password
            });
            if (response.errno === 0) {
                Toast.success('密码修改成功');
                setShowPasswordModal(false);
            } else {
                Toast.error(response.errstr || '密码修改失败');
            }
        } catch (error) {
            console.error('密码修改失败:', error);
            Toast.error('密码修改失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的账号');
            return;
        }
        try {
            const response = await request.post('/go/sp/subsp/batchedit_sub_sp/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '账号/用户名',
            dataIndex: 'account',
            width: 200,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>{record.name}</div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 320,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Button
                        type={record.status === 1 ? 'warning' : 'success'}
                        icon={record.status === 1 ? <IconStop /> : <IconPlayCircle />}
                        onClick={() => {
                            const uids = [record.uid];
                            if (record.status === 1) {
                                Modal.confirm({
                                    title: '确认禁用',
                                    content: '禁用后该账号将立即退出登录，是否确定？',
                                    okText: '确定',
                                    cancelText: '取消',
                                    onOk: () => handleBatchEdit(2, uids)
                                });
                            } else {
                                handleBatchEdit(1, uids);
                            }
                        }}
                    >
                        {record.status === 1 ? '禁用' : '启用'}
                    </Button>
                    <Button
                        type='tertiary'
                        icon={<IconKey />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowPasswordModal(true);
                        }}
                    >
                        修改密码
                    </Button>
                    <Popconfirm
                        title="确定要删除该账号吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className={styles.subAccountsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索账号/用户名"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="账号状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增子账号</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' icon={<IconStop />} onClick={() => {
                                Modal.confirm({
                                    title: '确认禁用',
                                    content: `确认要禁用选中的 ${selectedRowKeys.length} 个账号吗？禁用后这些账号将立即退出登录。`,
                                    okText: '确定',
                                    cancelText: '取消',
                                    onOk: () => handleBatchEdit(2, selectedRowKeys)
                                });
                            }}>批量禁用</Button>
                            <Button type='primary' icon={<IconPlayCircle />} onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量启用</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个账号吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增子账号"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
            >
                <Form onSubmit={handleAdd}>
                    <Form.Input
                        field="account"
                        label="登录账号"
                        placeholder="请输入手机号码"
                        rules={[{ required: true, message: '请输入登录账号' }]}
                    />
                    <Form.Input
                        field="passwd"
                        label="登录密码"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="请输入密码"
                        rules={[{ required: true, message: '请输入密码' }]}
                        suffix={<Button
                            theme='borderless'
                            icon={showPassword ? <IconEyeOpened /> : <IconEyeClosed />}
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ color: 'var(--semi-color-text-2)' }}
                        />}
                    />
                    <Form.Input
                        field="name"
                        label="用户名"
                        placeholder="请输入用户名"
                        rules={[{ required: true, message: '请输入用户名' }]}
                    />
                    <Form.RadioGroup
                        field="status"
                        label="状态"
                        initValue={1}
                    >
                        {Object.entries(statusMap).map(([value, { text, type }]) => (
                            <Form.Radio key={value} value={parseInt(value)} style={{ width: '100px', display: 'flex', alignItems: 'center' }}>
                                <Button theme='light' type={type} size='small' style={{ width: '100%', cursor: 'pointer', pointerEvents: 'none' }}>{text}</Button>
                            </Form.Radio>
                        ))}
                    </Form.RadioGroup>
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowAddModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="编辑子账号"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
            >
                <Form
                    onSubmit={handleEdit}
                    initValues={{
                        name: currentRecord?.name,
                        status: currentRecord?.status
                    }}
                >
                    <Form.Input
                        field="name"
                        label="用户名"
                        placeholder="请输入用户名"
                        rules={[{ required: true, message: '请输入用户名' }]}
                    />
                    <Form.Select
                        field="status"
                        label="状态"
                        rules={[{ required: true, message: '请选择状态' }]}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Form.Select.Option key={value} value={parseInt(value)}>{text}</Form.Select.Option>
                        ))}
                    </Form.Select>
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="修改密码"
                visible={showPasswordModal}
                onCancel={() => setShowPasswordModal(false)}
                footer={null}
                centered
            >
                <Form onSubmit={handlePasswordChange}>
                    <Form.Input
                        field="password"
                        label="新密码"
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="请输入新密码（至少6位）"
                        rules={[{
                            required: true,
                            message: '请输入新密码',
                            validator: (rule, value) => {
                                return value && value.length >= 6;
                            },
                            message: '密码长度不能少于6位'
                        }]}
                        suffix={<Button
                            theme='borderless'
                            icon={showNewPassword ? <IconEyeOpened /> : <IconEyeClosed />}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            style={{ color: 'var(--semi-color-text-2)' }}
                        />}
                    />
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowPasswordModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default SubAccounts;